import { CoffeePhoto } from '@/components/common/CoffeePhoto';
import { useImageFallback } from '@/hooks/useImageFallback';
import type { SearchCoffeeResult } from '@/types/search';

type SearchResultRowVariant = 'dropdown' | 'card';

interface SearchResultRowProps {
  result: SearchCoffeeResult;
  variant?: SearchResultRowVariant;
  highlighted?: boolean;
  onSelect?: () => void;
}

export function SearchResultRow({
  result,
  variant = 'dropdown',
  highlighted = false,
  onSelect,
}: SearchResultRowProps) {
  const roasterName = result.roaster?.name ?? 'Unknown roaster';
  const imageUrl = result.imageUrl ?? null;
  const { hasError, onError } = useImageFallback(imageUrl);
  // Mirrors ShelfCard/CoffeeDetail: no framed tile when there's no photo, and
  // a left divider before the copy only when a photo is present.
  const showImage = imageUrl !== null && !hasError;

  const dropdownClass = `flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
    highlighted ? 'bg-wolisho-brown/10' : 'hover:bg-wolisho-brown/5'
  }`;
  const cardClass =
    'block w-full rounded-xl bg-wolisho-card p-4 text-left transition-colors hover:bg-wolisho-card/80 focus-within:ring-2 focus-within:ring-wolisho-brown/40';

  const className = variant === 'card' ? cardClass : dropdownClass;

  const photo = showImage ? (
    <CoffeePhoto
      src={imageUrl}
      alt={result.name}
      sizeClassName={variant === 'card' ? 'w-16 h-20' : 'w-9 h-11'}
      radiusClassName={variant === 'card' ? undefined : 'rounded-lg'}
      onError={onError}
    />
  ) : null;

  // The dropdown row is itself a flex container (gap-3), so the photo and copy
  // sit as direct siblings.
  const dropdownContent = (
    <>
      {photo}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-black">{result.name}</div>
        <div className="mt-0.5 truncate text-sm">
          <span className="font-semibold text-black/55">{roasterName}</span>
          <span className="mx-1.5 text-black/35">·</span>
          <span className="text-black/55">{result.origin}</span>
        </div>
      </div>
    </>
  );

  const cardContent = (
    <div className="flex items-center gap-4">
      {photo}
      <div
        className={`min-w-0 flex-1 ${showImage ? 'border-l border-wolisho-brown/10 pl-4' : ''}`}
      >
        <p className="m-0 truncate font-bold text-black">{result.name}</p>
        <p className="m-0 mt-0.5 text-sm text-black/75">
          by <span className="font-semibold">{roasterName}</span>
        </p>
        <p className="m-0 mt-0.5 text-sm text-black/60">{result.origin}</p>
      </div>
    </div>
  );

  const content = variant === 'card' ? cardContent : dropdownContent;

  if (onSelect) {
    return (
      <button type="button" onClick={onSelect} className={className}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}
